/**
 * A small filled circle in a severity's color — the worklist's severity marker
 * since Ian swapped the repeated CRITICAL/WARNING pill for a dot (2026-09-28).
 *
 * Drawn with CSS rather than a "●" character so no font can substitute it.
 * Beside the severity's own word (a heading) it is decorative; standing alone
 * (a group row) it carries the word as its accessible name and hover title, so
 * nobody has to tell red from green by color alone.
 */
import React from "react"

import { THEME } from "../../../config/theme"

interface Props {
  severity: string
  /** True when the severity's word is already printed beside the dot. */
  decorative?: boolean
}

const SEVERITY_COLORS = THEME.problemDisplay.severityColors as Record<string, string>

/** "critical" → "Critical" — the registry's severities are lowercase slugs. */
export function severityWord(severity: string): string {
  return severity.charAt(0).toUpperCase() + severity.slice(1)
}

export default function SeverityDot({ severity, decorative = false }: Props) {
  const style: React.CSSProperties = {
    display: "inline-block",
    width: 10,
    height: 10,
    borderRadius: THEME.borderRadius.pill,
    background: SEVERITY_COLORS[severity] ?? THEME.colors.semantic.info,
    flex: "none",
  }
  if (decorative) {
    return <span aria-hidden="true" style={style} />
  }
  const word = severityWord(severity)
  return <span role="img" aria-label={word} title={word} style={style} />
}
